import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function GlobalSearch() {
  const { api, token } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const searchDebounced = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(response.data.results);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [api]
  );

  useEffect(() => {
    searchDebounced(query);
  }, [query, searchDebounced]);

  const handleSelect = (result) => {
    // Navigate to Family View with HOF ID
    const hofId = result.hof_id || result.id;
    navigate(`/family/${hofId}`);
    setQuery('');
    setIsOpen(false);
  };

  if (!token) return null;

  return (
    <div className="relative" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search name, ITS, mobile, thaali..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-9 w-64 md:w-80 bg-slate-100 border-0 focus:bg-white"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border max-h-96 overflow-auto z-50 animate-fade-in">
          {loading ? (
            <div className="p-4 text-center">
              <div className="w-5 h-5 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              {query.length < 2 ? 'Type at least 2 characters...' : 'No results found'}
            </div>
          ) : (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={`${result.id}-${index}`}
                  onClick={() => handleSelect(result)}
                  className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`search-result-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800">{result.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {result.its_id && <span className="font-mono">ITS: {result.its_id} | </span>}
                        HOF: {result.hof_name} | {result.mobile}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <Badge variant="outline" className="text-xs">
                        {result.type === 'family' ? 'Family' : 'Member'}
                      </Badge>
                      {result.mohalla && (
                        <p className="text-xs text-slate-400 mt-1">{result.mohalla}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
